import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import { Avatar, Box, Chip, Divider, Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { gateway } from "../gateway";
import { nextViewConnectBusinessState, previousViewConnectBusinessState } from "../redux/features/connectBusinessStateSlice";
import CheckExperienceResidential from "./checkboxExperienceResidential";
import { connectBusinessFlowSx } from "./connectBusinessFlowStyles";
import { NextButton } from "./nextButton";

export const ConnectBusinessResidential = ({ checkedState, nextViewComponentState, previousViewEntityState, updateComponentState }) => {
  const residencialType = "residencial";
  const [residencialServices, setResidencialServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const dispatch = useDispatch();

  const selectedCount = Object.values(checkedState || {}).filter(Boolean).length;
  const buttonText = selectedCount > 0 ? "Next" : "Skip";

  const getWorkServiceByType = async (type) => {
    try {
      setLoading(true);
      setLoadError("");
      const response = await gateway.get(`/workservices?type=${type}`);
      const data = response?.data?.data?.items ?? [];
      setResidencialServices(data);
    } catch (error) {
      console.error(error);
      setLoadError("We couldn’t load the services. Please try again.");
      setResidencialServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (serviceId, isChecked) => {
    dispatch(
      updateComponentState({
        residentialServices: {
          ...checkedState,
          [serviceId]: isChecked,
        },
      }),
    );
  };

  useEffect(() => {
    getWorkServiceByType(residencialType);
  }, []);

  return (
    <Box
      sx={{
        ...connectBusinessFlowSx.shell,
      }}
    >
      <Grid container sx={{ width: "100%", mx: "auto" }}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              ...connectBusinessFlowSx.panel,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: "center" }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  ...connectBusinessFlowSx.iconWrap,
                }}
              >
                <HomeIcon sx={{ fontSize: 28 }} />
              </Avatar>

              <Typography variant="h5" sx={connectBusinessFlowSx.title} gutterBottom>
                Residential Experience
              </Typography>

              <Typography variant="body1" sx={connectBusinessFlowSx.copy}>
                What experience areas are you looking for?
              </Typography>

              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
                <Chip
                  icon={<CheckCircleOutlineIcon />}
                  label={`${selectedCount} selected`}
                  variant="outlined"
                  sx={{ borderColor: "var(--fx-surface-border, #E2E8F0)", borderRadius: "3px" }}
                />
              </Stack>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Content */}
            <Box
              sx={{
                ...connectBusinessFlowSx.contentBand,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2, display: "block" }}>
                Select one or more areas
              </Typography>

              {loading ? (
                <Stack spacing={1.5}>
                  <Skeleton variant="rounded" height={44} />
                  <Skeleton variant="rounded" height={44} />
                  <Skeleton variant="rounded" height={44} />
                </Stack>
              ) : loadError ? (
                <Typography variant="body2" color="error">
                  {loadError}
                </Typography>
              ) : residencialServices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No services available right now. You can skip and continue.
                </Typography>
              ) : (
                <CheckExperienceResidential
                  title="Residential Cleaning Services"
                  data={residencialServices}
                  checkedState={checkedState}
                  onToggle={handleToggle}
                />
              )}
            </Box>

            {/* Footer */}
            <Grid container alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {selectedCount > 0 ? "Great! Continue to see your best matches." : "You can skip."}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} sx={{ mt: { xs: 1.5, sm: 0 } }}>
                <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                  <NextButton
                    nextViewEntityState={nextViewConnectBusinessState}
                    previousViewEntityState={previousViewConnectBusinessState}
                    buttonText={buttonText}
                    sx={{
                      borderRadius: 32,
                      "& .MuiButton-root": {
                        borderRadius: 32,
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
